import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { categories, manhwaComics } from '@/mocks/manhwa';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find(c => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-emotion-sad-line text-foreground-400 text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">分类未找到</h1>
            <p className="text-sm text-foreground-500 mt-2">请检查链接是否正确</p>
            <Link to="/" className="mt-6 inline-block px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const comics = manhwaComics.filter(c => c.category === category.name);

  return (
    <>
      <Navbar />
      <main className="min-h-screen">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: category.name },
          ]}
        />

        {/* Category Header */}
        <section className="w-full py-10 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center shrink-0">
                <i className={`${category.icon} text-primary-600 text-2xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-2 max-w-xl">
                  {category.description}
                </p>
              </div>
              <div className="md:ml-auto shrink-0">
                <span className="text-sm text-foreground-400">
                  共 <strong className="text-foreground-900">{comics.length}</strong> 部作品
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Content Area: Sidebar + Grid */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Sidebar - Category Navigation */}
              <aside className="lg:w-56 shrink-0">
                <div className="lg:sticky lg:top-24">
                  <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">全部分类</h3>
                  <nav className="space-y-1">
                    {categories.map((cat) => {
                      const catComicCount = manhwaComics.filter(c => c.category === cat.name).length;
                      const isActive = cat.slug === slug;
                      return (
                        <Link
                          key={cat.id}
                          to={`/category/${cat.slug}`}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 group ${
                            isActive
                              ? 'bg-primary-50 text-primary-600 font-medium border border-primary-100'
                              : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900 border border-transparent'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                            isActive ? 'bg-primary-100' : 'bg-background-100 group-hover:bg-background-200/70'
                          }`}>
                            <i className={`${cat.icon} text-sm ${isActive ? 'text-primary-600' : 'text-foreground-400'}`}></i>
                          </div>
                          <span className="flex-1 whitespace-nowrap">{cat.name}</span>
                          <span className={`text-xs ${isActive ? 'text-primary-400' : 'text-foreground-300'}`}>
                            {catComicCount}
                          </span>
                        </Link>
                      );
                    })}
                  </nav>
                </div>
              </aside>

              {/* Main Grid */}
              <div className="flex-1 min-w-0">
                {comics.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                    {comics.map((comic) => (
                      <ComicCard
                        key={comic.id}
                        id={comic.id}
                        title={comic.title}
                        author={comic.author}
                        status={comic.status}
                        rating={comic.rating}
                        coverImage={comic.coverImage}
                        category={comic.category}
                        tags={comic.tags}
                        chapters={comic.chapters}
                        slug={comic.slug}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                      <i className="ri-book-open-line text-foreground-400 text-2xl"></i>
                    </div>
                    <p className="text-sm text-foreground-500">该分类暂无漫画，请稍后再来</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${category.name} - 韩漫漫画网站`,
          description: category.description,
          url: `https://www.example.com/category/${category.slug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '韩漫漫画网站',
            url: 'https://www.example.com',
          },
        })}
      </script>
    </>
  );
}